import { useTranslation } from 'react-i18next';
import { Store } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { BlockConceptsViewDto } from '@subscriptions/application/dtos';

interface BlockConceptsTableProps {
  concepts: BlockConceptsViewDto;
}

const ROW_GRID = 'grid grid-cols-[minmax(0,3fr)_40px_minmax(96px,auto)] gap-3';

/**
 * Charge breakdown of the next payment, grouped by store when the block holds more than one.
 *
 * The rows scroll inside the card while the header and the total stay out of the scroll, so the
 * total is readable no matter how many stores the block carries.
 */
export const BlockConceptsTable = ({ concepts }: BlockConceptsTableProps) => {
  const { t } = useTranslation('subscriptions');
  const { groups, conceptCount, totalLabel } = concepts;
  const [firstGroup] = groups;
  const isGrouped = firstGroup?.isGrouped ?? false;

  return (
    <div className="overflow-hidden rounded-lg border border-border font-sans">
      <div className={cn(ROW_GRID, 'h-11 items-center bg-muted px-3.5 text-xs font-medium text-muted-foreground')}>
        <div>{t('blockDetail.summary.concepts.concept')}</div>
        <div className="text-center">{t('blockDetail.summary.concepts.quantity')}</div>
        <div className="text-right">{t('blockDetail.summary.concepts.amount')}</div>
      </div>

      <div className="max-h-[100dvh] overflow-y-auto">
        {groups.map((group) => (
          <div className="border-t border-border" key={group.storeUuid}>
            {group.isGrouped && (
              <div className="flex items-center gap-2 bg-muted px-3.5 py-2.5">
                <Store className="h-3.5 w-3.5" />
                <span className="text-sm font-semibold">{group.storeName}</span>
              </div>
            )}

            {group.rows.map((row) => (
              <div className={cn(ROW_GRID, 'items-start border-t border-border p-3.5 text-sm')} key={row.key}>
                <div className="min-w-0">
                  <div className="font-medium">{row.name}</div>
                  {row.description && <div className="mt-0.5 text-xs text-muted-foreground">{row.description}</div>}
                  {row.periodLabel && <div className="mt-1 text-xs">{row.periodLabel}</div>}
                </div>
                <div className="text-center tabular-nums">{row.quantityLabel}</div>
                <div className="whitespace-nowrap text-right font-medium tabular-nums">{row.amountLabel}</div>
              </div>
            ))}

            {group.isGrouped && (
              <>
                <div className={cn(ROW_GRID, 'items-center border-t border-border px-3.5 py-3 text-sm')}>
                  <div className="min-w-0 font-medium">{t('blockDetail.summary.concepts.discount')}</div>
                  <div />
                  <div
                    className={cn('whitespace-nowrap text-right font-medium tabular-nums', {
                      'text-success': group.hasDiscount,
                      'text-muted-foreground': !group.hasDiscount,
                    })}
                  >
                    {group.discountLabel}
                  </div>
                </div>

                <div className={cn(ROW_GRID, 'items-center border-t border-border px-3.5 py-2.5 text-sm')}>
                  <div className="min-w-0 text-muted-foreground">
                    {t('blockDetail.summary.concepts.subtotal', { store: group.storeName })}
                  </div>
                  <div />
                  <div className="whitespace-nowrap text-right font-semibold tabular-nums">{group.subtotalLabel}</div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {!isGrouped && firstGroup && (
        <div className={cn(ROW_GRID, 'items-start border-t border-border p-3.5 text-sm')}>
          <div className="min-w-0">
            <div className="font-medium">{t('blockDetail.summary.concepts.globalDiscount')}</div>
            {!firstGroup.hasDiscount && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t('blockDetail.summary.concepts.noDiscounts')}
              </div>
            )}
          </div>
          <div />
          <div
            className={cn('whitespace-nowrap text-right font-medium tabular-nums', {
              'text-success': firstGroup.hasDiscount,
              'text-muted-foreground': !firstGroup.hasDiscount,
            })}
          >
            {firstGroup.discountLabel}
          </div>
        </div>
      )}

      <div className={cn(ROW_GRID, 'items-center border-t border-border bg-muted p-3.5')}>
        <div className="min-w-0">
          <div className="text-sm font-bold">{t('blockDetail.summary.concepts.total')}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {conceptCount === 1
              ? t('blockDetail.summary.concepts.conceptCountOne')
              : t('blockDetail.summary.concepts.conceptCountOther', { count: conceptCount })}{' '}
            · {t('blockDetail.summary.taxIncluded')}
          </div>
        </div>
        <div />
        <div className="whitespace-nowrap text-right text-sm font-bold tabular-nums">{totalLabel}</div>
      </div>
    </div>
  );
};
